import { h } from 'preact';
import { useRef, useState } from 'preact/hooks';
import InputMask from 'react-input-mask';
import styled from 'styled-components';
import { breakpoints, colors } from '../../util/variables';
import { ShowPassword, HidePassword, WarningIcon } from '../../icons';
import { FormControl } from './FormControl';
import Interrogation from '../../icons/Interrogation';
import { useEffect } from 'react';


interface TextFieldProps {
	id?: string;
	label: string;
	placeholder?: string;
	value?: string;
	required?: boolean;
	variant?: 'password' | 'text';
	mask?: string;
	maskPlaceholder?: string;
	type?: 'password' | 'text' | 'tel' | 'date';
	error?: any;
	warningInfo?: string;
	onBlur?: (e: any) => void;
	onFocus?: (e: any) => void;
	onChange?: (ev: any) => void;
	toolTip?: string;
}

const Error = styled.div`
	color: ${colors.red};
	font-weight: 500;
	font-size: 10px;
	line-height: 12px;
	min-height: 16px;
`;

const ToolTipLeftPosition = {
	top: -64,
	left: 86
}

const ToolTipRigthPosition = {
	top: -80,
	left: -176
}

const EndAdornment = styled.div`
	position: absolute;
	top: 40%;
	right: 10px;
	transform: translateY(-40%);
`;

const InputWrapper = styled.div`
	position: relative;
	padding-top: 15px;
`;

const Input = styled.input<{ error: any }>`
	border: 0;
	border-bottom: 2px solid ${(props) => (props.error ? `${colors.red} !important` : `#7f7f7f`)};
	border-radius: 0;
	color: #636363;
	font-size: 16px;
	line-height: 16px;
	padding: 8px 0;
	width: 100%;

	::placeholder {
		color: #bdbdbd;
	}

	:focus {
		outline: none;
		border-bottom: 2px solid #636363;
	}

	@media (min-width: 768px) {
	}
`;

const Label = styled.label<{ isActive: boolean; error: boolean }>`
	color: ${({ error }) => (error ? colors.red : 'inherit')};
	display: block;
	font-family: Inter;
	font-style: normal;
	font-weight: 500;
	font-size: ${(props: { isActive: boolean }) => (props.isActive ? '10px' : '16px')};
	line-height: 1;
	margin-bottom: 5px;
	width: auto;
	position: absolute;
	left: 0;
	top: ${(props: { isActive: boolean }) => (props.isActive ? '3px' : '15px')};
	transition: all 0.15s ease-in-out;
`;

const WarningInfoStyled = styled.span<{ isActive: boolean, position: any }>`
	svg {
		margin-left: 5px;
		margin-bottom: -3px;
	}
	span {
		background: #FFFFFF;
		border: 1px solid #DCDCDC;
		box-sizing: border-box;
		box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.15);
		font-family: Nunito Sans;
		font-style: normal;
		font-weight: normal;
		font-size: 12px;
		line-height: 16px;
		letter-spacing: 0.02em;
		padding: 12px 16px;
    width: 199px;
    display: inline-block;
		position: absolute;
		top: -65px;
    margin-left: 6px;
		display: ${(props: { isActive: boolean }) => (props.isActive ? 'inline-block' : 'none')};
	}

	@media screen and (max-width: ${breakpoints.md}) {
		span{
			top: ${({ position }) => position.top}px;
			left: ${({ position }) => position.left}px;
			width: 220px;
		}
	}

`;

export const TextField = ({
	id,
	label,
	error,
	type,
	value,
	required,
	variant = 'text',
	mask,
	maskPlaceholder,
	warningInfo,
	toolTip,
	onChange = (e) => null,
	onBlur = (e) => null,
	onFocus = (e) => null,
	...props
}: TextFieldProps) => {
	const [isEnabledWarningInfo, setIsEnabledWarningInfo] = useState(false);
	const [showText, setShowText] = useState<boolean>(variant !== 'password');
	const [isFocus, setIsFocus] = useState<boolean>(!!value);
	const [maskedValue, setMaskedValue] = useState<string | number>('');
	const HAS_MASK = useState<boolean>(!!mask);
	const input = useRef<HTMLInputElement>(null);
	const [showToolTip, setShowToolTip] = useState(false);

	const showToolTipDialog = () => {
		setShowToolTip(!showToolTip);
	}

	const handleFocus = (e: any) => {
		setIsFocus(true);
		onFocus(e);
		setIsEnabledWarningInfo(true);
	};

	const handleBlur = (e: any) => {
		onBlur(e);
		if (HAS_MASK && maskedValue === maskPlaceholder) {
			setIsFocus(false);
		}

		if (!input.current?.value) {
			setIsFocus(false);
		}

		setIsEnabledWarningInfo(false);
	};

	const handleActive = (e: any) => {
		if (input.current) {
			(input as any).current.focus();
		}
		setIsFocus(true);
	};

	const handleMaskEvent = ({ nextState }: any) => {
		const { value } = nextState;
		setMaskedValue(value);
		return nextState;
	};

	useEffect(() => {
		if (input.current?.value) {
			setIsFocus(true);
		}
	}, [value]);

	return (
		<FormControl id={id} label={label} required={required}>
			<InputWrapper>
				{mask ? (
					<InputMask
						mask={mask}
						maskPlaceholder={maskPlaceholder}
						beforeMaskedStateChange={handleMaskEvent}
						{...props}
						onChange={onChange}
						onBlur={(e: any) => handleBlur(e)}
						onFocus={(e: any) => handleFocus(e)}
						value={value}
					>
						<Input ref={input} type={showText ? 'text' : type} error={error} id={id} value={value} />
					</InputMask>
				) : (
					<Input
						ref={input}
						type={showText ? 'text' : type}
						error={error}
						{...props}
						onChange={onChange}
						onFocus={(e: any) => handleFocus(e)}
						onBlur={(e: any) => handleBlur(e)}
						value={value}
						id={id}
					/>
				)}
				<Label
					onClick={handleActive}
					isActive={isFocus}
					htmlFor={id}
					className="mwc-label"
					error={error}
				>
					{label}
					{required ? '*' : <small> (Optional)</small>}
					{warningInfo && (
						<WarningInfoStyled isActive={isEnabledWarningInfo} position={ToolTipLeftPosition}>
							<span>{warningInfo}</span>
							<WarningIcon></WarningIcon>
						</WarningInfoStyled>
					)}

				</Label>
				<Error>{error && <span role="alert">{error?.message}</span>}</Error>
				{variant == 'password' && (
					<EndAdornment onClick={() => setShowText(!showText)}>
						{showText ? <ShowPassword /> : <HidePassword />}
					</EndAdornment>
				)}
				{toolTip && (
					<EndAdornment onClick={() => showToolTipDialog()}>
						<Interrogation />
						<WarningInfoStyled isActive={showToolTip} position={ToolTipRigthPosition} >
							<span>{toolTip}</span>
						</WarningInfoStyled>
					</EndAdornment>
				)}
			</InputWrapper>
		</FormControl>
	);
};
